import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, takeUntil } from 'rxjs/operators';

import { CompanyPlanTypes } from '@site-mate/sitemate-global-shared';

import { ActivityService } from 'app/home/activity/activity.service';
import { HomePagePeopleBaseComponent } from 'app/home/home-people-base.component';
import { NewWorkspaceModalComponent } from 'app/org/new-workspace-modal/new-workspace-modal.component';
import { SegmentService } from 'app/segment/segment.service';
import { LocalStorageKeys } from 'app/shared/model/local-storage-keys.enum';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { LocalStorageService } from 'app/shared/service/local-storage.service';
import { TeamService } from 'app/shared/service/team.service';
import { BusinessAttributesSelectorComponent } from 'app/user/business-attributes-selector/business-attributes-selector.component';
import { SignUpCompanyBrandingComponent } from 'app/user/sign-up-simplified/sign-up-steps/sign-up-company-branding/sign-up-company-branding.component';
import { SignUpService } from 'app/user/sign-up-simplified/sign-up.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-company-home-activity',
  templateUrl: 'company-home-activity.component.html',
  styleUrls: ['home-page.component.scss'],
})
export class CompanyHomeActivityComponent extends HomePagePeopleBaseComponent implements OnInit, OnDestroy {
  companyId: string;
  title: string;
  isTrialUser: boolean;
  activityLevel = 'org';

  constructor(
    private readonly teamService: TeamService,
    protected readonly activityService: ActivityService,
    protected readonly errorHandler: ErrorHandler,
    public readonly userService: UserService,
    protected readonly segmentService: SegmentService,
    private readonly localStorageService: LocalStorageService,
    private readonly modal: NgbModal,
    private readonly signupService: SignUpService,
  ) {
    super(errorHandler, segmentService, activityService);
  }

  ngOnInit(): void {
    this.teamService.currentTeam
      .pipe(
        takeUntil(this.unsub),
        filter((company) => company.isCompany && company.id),
      )
      .subscribe((company) => {
        this.companyId = company.id;
        this.title = company.name;
        this.isTrialUser = company.companyMetadata?.billing?.planType === CompanyPlanTypes.FreeTrial;
        this.reloadActivities();
        this.showNewWorkspaceConfigModal();
      });
  }

  loadActivitiesCore() {
    return this.activityService.getCompanyUserActivities(
      this.companyId,
      this.activityLimit,
      this.activityOffset,
    );
  }

  ngOnDestroy(): void {
    this.unsub.next();
    this.unsub.complete();
  }

  private showNewWorkspaceConfigModal() {
    const isRedirectedAfterWorkspaceCreation =
      this.localStorageService.getItem(LocalStorageKeys.CreatingWorkspaceForOrgController) === 'true';
    if (isRedirectedAfterWorkspaceCreation) {
      this.displaySignUpCompanyBrandingComponent();
    }
  }

  displaySignUpCompanyBrandingComponent() {
    const modalRef = this.modal.open(SignUpCompanyBrandingComponent, {
      size: 'lg',
      windowClass: 'modal-400',
      modalDialogClass: 'modal-dialog-centered',
      backdrop: 'static',
      keyboard: false,
      animation: false,
    });

    void modalRef.result
      .then(() => {
        this.openBusinessAttributeModal();
      })
      .catch(() => {})
      .finally(() => {
        this.localStorageService.removeItem(LocalStorageKeys.CreatingWorkspaceForOrgController);
      });
  }

  private openBusinessAttributeModal() {
    const modalRef = this.modal.open(BusinessAttributesSelectorComponent, {
      size: 'xl',
      windowClass: 'modal-900',
      modalDialogClass: 'modal-dialog-centered redirect-from-workspace-creation',
      animation: false,
    });
    modalRef.componentInstance.isFromWorkspaceCreation = true;

    void modalRef.result.then(() => {
      this.openNewWorkspaceModal();
    });
  }

  private openNewWorkspaceModal() {
    this.signupService.popConfetti();
    void this.modal.open(NewWorkspaceModalComponent, {
      size: 'lg',
      windowClass: 'modal-500',
      modalDialogClass: 'modal-dialog-centered',
      backdrop: 'static',
      keyboard: false,
      animation: false,
    });
  }
}
